import { useTheme } from "@mui/material";
import Konva from "konva";
import { Arrow, Group, Label, Tag, Text } from "react-konva";
import { openArrowContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { selectViewedRectangles } from "@store/selectors";
import { ICanvasArrow, RectangleGeometry } from "@/types/canvasTypes";
import { addMargin, rectsHaveIntersection , dashStateForCredence } from "@shared/utils/utils";

interface CanvasArrowProps extends ICanvasArrow {
  startRectIdx: number;
}

interface ArrowOnRectangleParams {
  angle: number;
  rectangle: RectangleGeometry;
  determineX: (y: number) => number;
  determineY: (x: number) => number;
}

const getRectangleCenter = ({ x, y, width, height }: RectangleGeometry) => ({
  x: x + width / 2,
  y: y + height / 2,
});

const calculateY = (c: number) => (tangent: number) => (x: number) =>
  tangent * x + c;

const calculateX = (c: number) => (tangent: number) => (y: number) =>
  (y - c) / tangent;

function isRightSide(angle: number, baseAngle: number) {
  return (
    (angle <= baseAngle && angle > 0) || (angle <= 0 && angle > -baseAngle)
  );
}

function isTopSide(angle: number, baseAngle: number) {
  return angle > baseAngle && angle <= Math.PI - baseAngle;
}

function isLeftSide(angle: number, baseAngle: number) {
  return (
    (angle <= Math.PI && angle > Math.PI - baseAngle) ||
    (angle <= -Math.PI + baseAngle && angle > -Math.PI)
  );
}

const calculateArrowOnRectangle = ({
  angle,
  rectangle: { x: left, y: top, width, height },
  determineX,
  determineY,
}: ArrowOnRectangleParams): [number, number] => {
  const right = left + width;
  const bottom = top + height;
  const baseAngle = Math.atan(height / width);

  if (isRightSide(angle, baseAngle)) {
    return [left, determineY(left)];
  }

  if (isTopSide(angle, baseAngle)) {
    return [determineX(top), top];
  }

  if (isLeftSide(angle, baseAngle)) {
    return [right, determineY(right)];
  }
  return [determineX(bottom), bottom];
};

/**
 * @param startRect x, y, width, height
 * @param endRect x, y, width, height
 * @returns [startX, startY, endX, endY]
 */
export const calculateArrowPoints = (
  startRect: RectangleGeometry,
  endRect: RectangleGeometry
): [number, number, number, number] => {
  const { x: startCenterX, y: startCenterY } = getRectangleCenter(startRect);

  const { x: endCenterX, y: endCenterY } = getRectangleCenter(endRect);

  const dx = startCenterX - endCenterX;
  const dy = startCenterY - endCenterY;

  const tangent = dy / dx;

  const c = -tangent * startCenterX + startCenterY;

  if (tangent === Infinity) {
    return [startCenterX, startRect.y, endCenterX, endRect.y + endRect.height];
  }

  if (tangent === -Infinity) {
    return [
      startCenterX,
      startRect.y + startRect.height,
      endCenterX,
      endRect.y,
    ];
  }

  const determineY = calculateY(c)(tangent);
  const determineX = calculateX(c)(tangent);

  const angle = Math.atan2(dy, dx);

  return [
    ...calculateArrowOnRectangle({
      angle,
      rectangle: startRect,
      determineX,
      determineY,
    }),
    ...calculateArrowOnRectangle({
      angle: angle > 0 ? angle - Math.PI : angle + Math.PI,
      rectangle: endRect,
      determineX,
      determineY,
    }),
  ];
};

const CanvasArrow = ({
  startRectIdx,
  endRectIdx,
  tag,
  kind,
  credence,
}: CanvasArrowProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const rectangles = useAppSelector(selectViewedRectangles);
  const startRect = rectangles[startRectIdx];
  const endRect = rectangles[endRectIdx];

  if (!startRect || !endRect) return null;

  // Guard on the margin-expanded rects, not the raw ones: when two
  // nodes nearly touch, the expanded boxes overlap and the endpoint
  // math returns crossed points — a tiny stub drawn underneath both
  // nodes, invisible until one is moved away. Treat "closer than the
  // margin" the same as overlapping: draw nothing.
  const startBox = addMargin(startRect, 5);
  const endBox = addMargin(endRect, 5);
  if (rectsHaveIntersection(startBox, endBox)) return null;

  let [startX, startY, endX, endY] = calculateArrowPoints(startBox, endBox);

  // If there's another (non-ghost) edge pointing the opposite direction
  // between the same pair of rectangles, shift both endpoints perpendicular
  // to the line so the two edges render as parallel offset lines rather than
  // overlapping. The CCW perpendicular (-dy, dx) naturally points in
  // opposite directions for the two siblings (because their line vectors are
  // negated), so no extra sign flipping is needed.
  const hasSibling = rectangles[endRectIdx]?.arrows.some(
    (a) => a.endRectIdx === startRectIdx && !a.doNotRender
  );
  if (hasSibling) {
    const lineDx = endX - startX;
    const lineDy = endY - startY;
    const len = Math.sqrt(lineDx * lineDx + lineDy * lineDy);
    if (len > 0) {
      const magnitude = 7;
      const perpX = (-lineDy / len) * magnitude;
      const perpY = (lineDx / len) * magnitude;
      startX += perpX;
      startY += perpY;
      endX += perpX;
      endY += perpY;
    }
  }

  const dx = startX - endX;
  const dy = startY - endY;

  // Hide the pointer only on genuinely tiny edges. (This used to read
  // `startY - endX` — a typo that made the arrowhead vanish whenever a
  // node's y happened to sit near the other node's x, "fixing itself"
  // when the node was moved.)
  const isPointerShown = Math.sqrt(dx ** 2 + dy ** 2) >= 20;

  const dashState = dashStateForCredence(credence);

  const textX = (startX + endX) / 2;
  const textY = (startY + endY) / 2 - 5;

  const handleContextMenu = (
    e: Konva.KonvaEventObject<MouseEvent>,
    startRectIdx: number,
    endRectIdx: number
  ) => {
    e.evt.preventDefault();
    dispatch(openArrowContextMenu({ idx: [startRectIdx, endRectIdx] }));
  };

  return (
    <Group
      onContextMenu={(e) => handleContextMenu(e, startRectIdx, endRectIdx)}
    >
      <Arrow
        points={[startX, startY, endX, endY]}
        stroke={theme.palette.text.primary}
        strokeWidth={kind === "bidirectional" ? 2.5 : 1.5}
        fill={theme.palette.text.primary}
        pointerAtEnding={
          (kind === "directed" || kind === "bidirectional") && isPointerShown
        }
        pointerAtBeginning={kind === "bidirectional" && isPointerShown}
        dashEnabled={dashState.enabled}
        dash={dashState.pattern}
        hitStrokeWidth={30}
      />
      <Label x={textX} y={textY}>
        <Tag fill={theme.palette.background.default} opacity={1} />
        <Text text={tag} wrap="none" fill={theme.palette.text.primary} />
      </Label>
    </Group>
  );
};

export default CanvasArrow;
